import React, { useEffect, useCallback, useState, useMemo } from 'react'
import get from 'lodash/get'
import {
  Channel,
  ChannelMemberResponse,
  logChatPromiseExecution,
  type MessageResponse,
  type Event as StreamEvent,
} from 'stream-chat'
import {
  MessageToSend,
  MessageInput,
  Window,
  Thread,
  useChatContext,
  useChannelActionContext,
  MessageInputProps,
} from 'stream-chat-react'
import { MessageList } from 'components/Chat/components/MessageList'
import { MessagingChannelHeader, FilesTab, StarredMessages } from 'components/Chat/components'
import RichTextMessagingInput from '../RichTextInput/RichTextMessagingInput'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getInitials, getInitialsFromName } from 'components/Chat/helper'
import { ReactComponent as NoSearchResultIcon } from 'assets/svgs/no-search-result-icon.svg'
import { Attachment } from 'stream-chat-react'
import { useGiphyContext } from 'components/Chat/Giphy'
import type { StreamChatGenerics } from 'components/Chat/types'
import { useUnreadContext } from 'components/Chat/contexts/UnreadContext'
import { IChatAttachment } from 'components/Chat'
import { getFileSizeFromUrl, guessImageFileTypeFromFileName } from 'utils/file-utils'
import { useUserInfo } from 'hooks/useUserInfo'
import { getChatRecipientData } from 'utils/helper'
import { IFileInfo } from 'types'
import { ampli } from 'ampli'
import { IStreamChatUserContext } from '../CreateChannel/CreateChannel'
import DOMPurify from 'dompurify'
import { renderText } from 'stream-chat-react'

export type ChannelInnerProps = {
  usersAssessmentResponseByQuestion: unknown
  chatUnreadMessageCountAction: (count: number) => void
  setShowChannelPreview: (showChannelPreview: boolean) => void
  theme: string
  showChannelPreview: boolean
  setShowDesktopUserProfile: (showDesktopUserProfile: boolean) => void
  showDesktopUserProfile: boolean
  saveUserFileAction: (streamMsgId: string, attachment: IChatAttachment) => void
  showChannelCreate?: boolean
  initialJumpMessageId?: string | null
  setInitialJumpMessageId?: (messageId: string | null) => void
}

export const ChannelInner = (props: ChannelInnerProps) => {
  const {
    theme,
    setShowChannelPreview,
    showChannelPreview,
    setShowDesktopUserProfile,
    showDesktopUserProfile,
    chatUnreadMessageCountAction,
    saveUserFileAction,
    showChannelCreate = false,
    initialJumpMessageId,
    setInitialJumpMessageId,
  } = props
  const { userName, userId, roles, organizationName } = useUserInfo()
  const [activeTab, setActiveTab] = useState('messages')
  const [searchResults, setSearchResults] = useState<
    Array<{ message: MessageResponse<StreamChatGenerics> }>
  >([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchActive, setIsSearchActive] = useState(false)
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null)
  const [pendingJumpMessageId, setPendingJumpMessageId] = useState<string | null>(null)

  const { giphyState, setGiphyState } = useGiphyContext()
  const { sendMessage } = useChannelActionContext<StreamChatGenerics>()
  const { channel, client } = useChatContext<StreamChatGenerics>()
  const { chatsUnreadCount, setChatsUnreadCount, setChannelMessageCount } = useUnreadContext()

  // Strongly-typed wrapper components to satisfy Stream's expected Input types
  type TypedMessageInputProps = MessageInputProps<StreamChatGenerics>

  // Memoize Input component types so Stream's MessageInput doesn't remount on every render
  const ThreadMessagingInput = useMemo(() => {
    const Comp: React.FC<TypedMessageInputProps> = (_threadProps) => (
      <RichTextMessagingInput showChannelCreate={false} groupFormatting={true} />
    )
    return Comp
  }, [])

  const MainMessagingInput = useMemo(() => {
    const Comp: React.FC<TypedMessageInputProps> = (_inputProps) => (
      <RichTextMessagingInput showChannelCreate={showChannelCreate} />
    )
    return Comp
  }, [showChannelCreate])

  const logSentMessage = (
    messageToSend: MessageToSend<StreamChatGenerics>,
    channel: Channel<StreamChatGenerics>
  ) => {
    const recipientData = getChatRecipientData(
      channel.state.members as unknown as Record<
        string,
        ChannelMemberResponse<IStreamChatUserContext>
      >,
      userId
    )

    ampli.messageSent({
      senderId: userId,
      content: messageToSend.text || '',
      senderName: userName,
      senderRoles: roles,
      recipientIds: recipientData.recipientIds,
      recipientNames: recipientData.recipientNames,
      recipientRoles: recipientData.recipientRoles,
      fileType: fileInfoArray.map((fileInfo) => fileInfo.fileType),
      fileSize: fileInfoArray.map((fileInfo) => fileInfo.fileSize),
      organizationName,
    })
  }

  const fileInfoArray: IFileInfo[] = []

  const saveAttachmentsIfNeeded = async (resp) => {
    if ((resp.message.attachments?.length || 0) > 0) {
      for (const attachment of resp.message.attachments || []) {
        let fileInfo: IFileInfo = {
          fileType: '',
          fileSize: '',
        } // Initialize fileInfo object for this attachment

        if (!['file', 'image'].includes(attachment.type || 'unknown')) continue

        if (attachment.type === 'file') {
          saveUserFileAction(resp.message.id, {
            title: attachment.title,
            fileSize: attachment.file_size?.toString() || '0',
            fileType: attachment.mime_type,
            url: attachment.asset_url,
          })

          fileInfo.fileType = attachment.mime_type
          fileInfo.fileSize = attachment.file_size?.toString() || '0'
        } else {
          const fileSize = await getFileSizeFromUrl(attachment.image_url!)
          saveUserFileAction(resp.message.id, {
            title: attachment.fallback,
            fileSize: fileSize.toString(),
            fileType: guessImageFileTypeFromFileName(attachment.fallback),
            url: attachment.image_url,
          })

          fileInfo.fileType = guessImageFileTypeFromFileName(attachment.fallback)
          fileInfo.fileSize = fileSize.toString()
        }

        fileInfoArray.push(fileInfo)
      }
    }
  }

  const overrideSubmitHandler = async (message: MessageToSend<StreamChatGenerics>) => {
    let updatedMessage

    if (message.attachments?.length && message.text?.startsWith('/giphy')) {
      const updatedText = message.text.replace('/giphy', '')
      updatedMessage = { ...message, text: updatedText }
    }

    if (giphyState) {
      const updatedText = `/giphy ${message.text}`
      updatedMessage = { ...message, text: updatedText }
    }

    if (channel) {
      const newMessage = updatedMessage || message
      const parentMessage = newMessage.parent

      const messageToSend = {
        ...newMessage,
        parent: parentMessage
          ? {
              ...parentMessage,
              created_at: parentMessage.created_at?.toString(),
              pinned_at: parentMessage.pinned_at?.toString(),
              updated_at: parentMessage.updated_at?.toString(),
            }
          : undefined,
      }

      if (get(channel, 'data.frozen')) {
        const payload = { ...messageToSend, attachments: [] }
        const sendMessagePromise = sendMessage(payload)
        logChatPromiseExecution(sendMessagePromise, 'send message')
        return
      }

      if (channel) {
        const messageResponse = await channel.sendMessage(messageToSend)
        await saveAttachmentsIfNeeded(messageResponse)
        logSentMessage(messageToSend, channel)
      }
    }

    setGiphyState(false)
  }

  const actions = ['delete', 'edit', 'react', 'reply']

  // Memoize the onClose callbacks to prevent unnecessary re-renders
  const handleFilesTabClose = useCallback(() => setActiveTab('messages'), [])
  const handleStarredMessagesClose = useCallback(() => setActiveTab('messages'), [])

  const handleSearchResults = useCallback(
    (
      results: Array<{ message: MessageResponse<StreamChatGenerics> }>,
      query: string,
      isActive: boolean
    ) => {
      setSearchResults(results)
      // setSearchQuery(query)
      setIsSearchActive(isActive)
    },
    []
  )

  // Handle initial jump from channel list search click
  useEffect(() => {
    if (initialJumpMessageId && channel && setInitialJumpMessageId) {
      // Set pending jump message ID - this will trigger the jump logic
      setPendingJumpMessageId(initialJumpMessageId)
      setInitialJumpMessageId(null) // Clear the initial jump ID so it doesn't trigger again
    }
  }, [initialJumpMessageId, channel, setInitialJumpMessageId])

  // Handle pending jump - this works for both search results and channel list clicks
  useEffect(() => {
    if (pendingJumpMessageId && channel) {
      const performJump = async () => {
        try {
          // Check if the message is already in the current loaded messages
          const existingMessage = channel.state.messages.find(
            (msg) => msg.id === pendingJumpMessageId
          )
          if (existingMessage) {
            // Message is already loaded, just highlight it
            setHighlightedMessageId(pendingJumpMessageId)
            setTimeout(() => {
              setHighlightedMessageId(null)
            }, 4000)
            setPendingJumpMessageId(null)
            return
          }

          // Try to load the message into the current state
          try {
            await channel.state.loadMessageIntoState(pendingJumpMessageId)
          } catch (loadError) {
            // If the message is not in current state, query it
            try {
              const messageResponse = await channel.query({
                messages: { id: pendingJumpMessageId },
              } as any)
              if (messageResponse.messages.length > 0) {
                await channel.state.loadMessageIntoState(pendingJumpMessageId)
              } else {
                // Fallback: fetch the single message and inject it into the channel state
                try {
                  const fetched = await client.getMessage(pendingJumpMessageId as any)
                  if (fetched?.message) {
                    // Inject the message so it can be rendered and scrolled to
                    channel.state.addMessageSorted(fetched.message as any, true)
                  } else {
                    setPendingJumpMessageId(null)
                    return
                  }
                } catch {
                  setPendingJumpMessageId(null)
                  return
                }
              }
            } catch (queryError) {
              // Fallback: fetch the single message and inject it into the channel state
              try {
                const fetched = await client.getMessage(pendingJumpMessageId as any)
                if (fetched?.message) {
                  channel.state.addMessageSorted(fetched.message as any, true)
                } else {
                  setPendingJumpMessageId(null)
                  return
                }
              } catch {
                setPendingJumpMessageId(null)
                return
              }
            }
          }

          // Set the highlighted message ID to trigger scroll
          setHighlightedMessageId(pendingJumpMessageId)
          setTimeout(() => {
            setHighlightedMessageId(null)
          }, 4000)

          setPendingJumpMessageId(null)
        } catch (error) {
          console.error('Failed to perform pending jump:', error)
          setPendingJumpMessageId(null)
        }
      }

      // Small delay to ensure DOM is fully updated and channel is ready
      setTimeout(performJump, 100)
    }
  }, [pendingJumpMessageId, channel, client])

  const handleJumpToMessage = useCallback(
    async (messageId: string) => {
      try {
        if (channel) {
          // Check if we're in search mode
          const isFromSearch = isSearchActive && searchResults.length > 0

          if (isFromSearch) {
            // For search results, set pending jump and clear search state
            setPendingJumpMessageId(messageId)
            setSearchResults([])
            setSearchQuery('')
            setIsSearchActive(false)
            return // Exit early, the useEffect will handle the jump
          }

          // For non-search jumps, handle immediately
          try {
            await channel.state.loadMessageIntoState(messageId)
          } catch (loadError) {
            // If the message is not in current state, query it
            try {
              const messageResponse = await channel.query({
                messages: { id: messageId },
              } as any)
              if (messageResponse.messages.length > 0) {
                await channel.state.loadMessageIntoState(messageId)
              } else {
                // Try to find the message in the current loaded messages
                const existingMessage = channel.state.messages.find((msg) => msg.id === messageId)
                if (!existingMessage) {
                  return
                }
              }
            } catch (queryError) {
              // Try to find the message in the current loaded messages
              const existingMessage = channel.state.messages.find((msg) => msg.id === messageId)
              if (!existingMessage) {
                return
              }
            }
          }

          // Set the highlighted message ID to trigger scroll
          setHighlightedMessageId(messageId)
          setTimeout(() => {
            setHighlightedMessageId(null)
          }, 4000)
        }
      } catch (error) {
        console.error('Failed to jump to message:', error)
      }
    },
    [channel, isSearchActive, searchResults]
  )

  // Helper function to sanitize HTML and ensure links open in new tab (same as MessageSimple)
  const sanitizeAndProcessLinks = (html: string): string => {
    const sanitized = DOMPurify.sanitize(html, {
      ADD_TAGS: ['mention'],
      ADD_ATTR: ['id', 'target', 'rel', 'href'],
    })

    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = sanitized

    const links = tempDiv.querySelectorAll('a')
    links.forEach((link) => {
      link.setAttribute('target', '_blank')
      link.setAttribute('rel', 'noopener noreferrer')
    })

    return tempDiv.innerHTML
  }

  // Function to highlight search terms (same as MessageSimple)
  const highlightSearchText = (text: string, query: string) => {
    if (!query || !query.trim() || !text) return text

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedQuery})`, 'gi')
    const parts = text.split(regex)

    return parts.map((part, index) => {
      if (regex.test(part)) {
        return (
          <span key={index} className="bg-app-yellow-40 text-app-black-100 rounded font-medium">
            {part}
          </span>
        )
      }
      return part
    })
  }

  // Function to highlight search terms in HTML while preserving HTML structure
  const highlightSearchInHtml = (html: string, query: string): string => {
    if (!query || !query.trim() || !html) return html

    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedQuery})`, 'gi')

    const walkTextNodes = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || ''
        if (regex.test(text)) {
          const span = document.createElement('span')
          const parts = text.split(regex)

          parts.forEach((part) => {
            if (regex.test(part)) {
              const highlight = document.createElement('span')
              highlight.className = 'bg-app-yellow-40 text-app-black-100 rounded font-medium'
              highlight.textContent = part
              span.appendChild(highlight)
            } else if (part) {
              span.appendChild(document.createTextNode(part))
            }
          })

          node.parentNode?.replaceChild(span, node)
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        Array.from(node.childNodes).forEach(walkTextNodes)
      }
    }

    walkTextNodes(tempDiv)
    return tempDiv.innerHTML
  }

  // Component to render search result messages using the same rendering logic as MessageSimple
  const SearchResultMessage = useCallback(
    ({
      message,
      searchQuery,
    }: {
      message: MessageResponse<StreamChatGenerics>
      searchQuery: string
    }) => {
      const isMyMessage = () => message.user?.id === client?.userID
      const hasText = message.text && message.text.trim().length > 0
      const hasAttachments = message.attachments && message.attachments.length > 0

      // Check for voice messages in attachments (same logic as MessageSimple)
      const voiceAttachments =
        hasAttachments && message.attachments
          ? message.attachments.filter((att) => {
              if (att.type !== 'file' && att.type !== 'audio') return false
              const allFields = JSON.stringify(att).toLowerCase()
              if (allFields.includes('voice-message')) return true
              if (att.mime_type?.startsWith('audio/')) return true
              const fileName =
                att.file_name || att.name || att.title || att.fallback || att.text || ''
              if (
                typeof fileName === 'string' &&
                (fileName.endsWith('.webm') || fileName.includes('voice-message'))
              )
                return true
              const url = att.asset_url || att.file_url || att.url || ''
              if (typeof url === 'string' && url.includes('voice-message')) return true
              if (att.mime_type?.includes('webm') || att.mime_type?.includes('audio')) return true
              return false
            })
          : []

      const nonVoiceAttachments =
        hasAttachments && message.attachments
          ? message.attachments.filter((att) => {
              if (att.type !== 'file' && att.type !== 'audio') return true
              const allFields = JSON.stringify(att).toLowerCase()
              if (allFields.includes('voice-message')) return false
              if (att.mime_type?.startsWith('audio/')) return false
              const fileName =
                att.file_name || att.name || att.title || att.fallback || att.text || ''
              if (
                typeof fileName === 'string' &&
                (fileName.endsWith('.webm') || fileName.includes('voice-message'))
              )
                return false
              const url = att.asset_url || att.file_url || att.url || ''
              if (typeof url === 'string' && url.includes('voice-message')) return false
              if (att.mime_type?.includes('webm') || att.mime_type?.includes('audio')) return false
              return true
            })
          : []

      return (
        <div className="str-chat__message str-chat__message-simple">
          <div className="mt-2 w-full relative">
            <div className={`flex gap-3 ${isMyMessage() ? 'justify-end' : 'justify-start'}`}>
              {!isMyMessage() && (
                <AppAvatar
                  size="md"
                  type="mixed"
                  shape="Rounded"
                  text={getInitials({
                    firstName: message.user?.firstName as string,
                    lastName: message.user?.lastName as string,
                  })}
                />
              )}
              <div
                className={`flex flex-col max-w-[75%] overflow-visible ${
                  isMyMessage() ? 'items-end' : 'items-start'
                }`}
              >
                <div
                  className={`text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1 ${
                    isMyMessage() ? 'mr-1 justify-end' : 'ml-1 justify-start'
                  }`}
                >
                  <span>
                    {isMyMessage() ? 'You' : `${message.user?.firstName} ${message.user?.lastName}`}
                  </span>
                </div>
                <div className="relative">
                  {voiceAttachments.length > 0 ? (
                    // Render voice message without background bubble
                    <div className="text-sm text-left">
                      {(hasText || nonVoiceAttachments.length > 0) && (
                        <div className={hasText ? 'mb-2' : ''}>
                          {(message.text ||
                            (message.custom as any)?.richTextHtml ||
                            message.html) && (
                            <div className="text-left">
                              {(message.custom as any)?.richTextHtml ? (
                                <div
                                  className="prose prose-sm max-w-none [&_a]:text-white [&_a]:underline [&_a:hover]:text-white/90 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-primary-10 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-primary-70 [&_mention]:font-medium"
                                  dangerouslySetInnerHTML={{
                                    __html: searchQuery
                                      ? highlightSearchInHtml(
                                          sanitizeAndProcessLinks(
                                            (message.custom as any).richTextHtml
                                          ),
                                          searchQuery
                                        )
                                      : sanitizeAndProcessLinks(
                                          (message.custom as any).richTextHtml
                                        ),
                                  }}
                                />
                              ) : message.html ? (
                                <div
                                  className="prose prose-sm max-w-none [&_a]:text-white [&_a]:underline [&_a:hover]:text-white/90 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-primary-10 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-primary-70 [&_mention]:font-medium"
                                  dangerouslySetInnerHTML={{
                                    __html: searchQuery
                                      ? highlightSearchInHtml(
                                          sanitizeAndProcessLinks(message.html || ''),
                                          searchQuery
                                        )
                                      : sanitizeAndProcessLinks(message.html || ''),
                                  }}
                                />
                              ) : searchQuery && message.text ? (
                                highlightSearchText(message.text, searchQuery)
                              ) : message.text ? (
                                renderText(message.text)
                              ) : null}
                            </div>
                          )}
                        </div>
                      )}
                      {nonVoiceAttachments.length > 0 && (
                        <div className="mt-2">
                          {nonVoiceAttachments.map((attachment, idx) => (
                            <Attachment key={idx} attachments={[attachment]} />
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Render regular message with bubble
                    <div
                      className={`rounded-2xl ${
                        isMyMessage() ? 'bg-app-primary-10 text-white' : 'bg-app-grey-5'
                      }`}
                    >
                      <div className="px-2 py-2 text-sm text-left">
                        {(hasText || (message.custom as any)?.richTextHtml || message.html) &&
                          message.text && (
                            <div className="text-left">
                              {(message.custom as any)?.richTextHtml ? (
                                <div
                                  className="prose prose-sm max-w-none [&_a]:text-white [&_a]:underline [&_a:hover]:text-white/90 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-primary-10 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-primary-70 [&_mention]:font-medium"
                                  dangerouslySetInnerHTML={{
                                    __html: searchQuery
                                      ? highlightSearchInHtml(
                                          sanitizeAndProcessLinks(
                                            (message.custom as any).richTextHtml
                                          ),
                                          searchQuery
                                        )
                                      : sanitizeAndProcessLinks(
                                          (message.custom as any).richTextHtml
                                        ),
                                  }}
                                />
                              ) : message.html ? (
                                <div
                                  className="prose prose-sm max-w-none [&_a]:text-white [&_a]:underline [&_a:hover]:text-white/90 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-primary-10 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-primary-70 [&_mention]:font-medium"
                                  dangerouslySetInnerHTML={{
                                    __html: searchQuery
                                      ? highlightSearchInHtml(
                                          sanitizeAndProcessLinks(message.html || ''),
                                          searchQuery
                                        )
                                      : sanitizeAndProcessLinks(message.html || ''),
                                  }}
                                />
                              ) : searchQuery && message.text ? (
                                highlightSearchText(message.text, searchQuery)
                              ) : message.text ? (
                                renderText(message.text)
                              ) : null}
                            </div>
                          )}
                        {nonVoiceAttachments.length > 0 && (
                          <div className={hasText ? 'mt-2' : ''}>
                            {nonVoiceAttachments.map((attachment, idx) => (
                              <Attachment key={idx} attachments={[attachment]} />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {isMyMessage() && (
                <AppAvatar
                  size="md"
                  type="mixed"
                  shape="Rounded"
                  text={getInitialsFromName(userName)}
                />
              )}
            </div>
          </div>
        </div>
      )
    },
    [client, userName]
  )

  useEffect(() => {
    const handlerNewMessageEvent = (event: StreamEvent) => {
      try {
        const { message, user } = event

        const msgCid = message?.cid
        if (user?.id !== client?.userID && channel?.cid !== msgCid && msgCid) {
          setChannelMessageCount((state) => ({
            ...state,
            [msgCid]: state[msgCid] ? state[msgCid] + 1 : 1,
          }))
          setChatsUnreadCount(chatsUnreadCount + 1)
          chatUnreadMessageCountAction(chatsUnreadCount + 1)
        }
      } catch (error: unknown) {
        console.log('error: ', error)
      }
    }

    client?.on('message.new', handlerNewMessageEvent)

    return () => client?.off('message.new', handlerNewMessageEvent)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, client, chatsUnreadCount, setChatsUnreadCount])

  return (
    <div className="w-full h-full flex channel-inner-container">
      <div className="main-thread-container w-full flex-shrink-0 min-w-0 h-full">
        <Window>
          <MessagingChannelHeader
            theme={theme}
            setShowChannelPreview={setShowChannelPreview}
            showChannelPreview={showChannelPreview}
            setShowDesktopUserProfile={setShowDesktopUserProfile}
            showDesktopUserProfile={showDesktopUserProfile}
            usersAssessmentResponseByQuestion={props.usersAssessmentResponseByQuestion}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onSearchResults={handleSearchResults}
            // searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
          {activeTab === 'messages' && (
            <>
              {/* Search Results */}
              {isSearchActive && searchResults.length > 0 && searchQuery.trim() && (
                <div className="px-4 py-2 bg-app-primary-10 border-b border-app-primary-20">
                  <p className="text-sm text-app-primary-100 font-medium">
                    Showing {searchResults.length} result{searchResults.length === 1 ? '' : 's'} for
                    "{searchQuery}"
                  </p>
                </div>
              )}

              {/* Search Result Components */}
              {isSearchActive && searchResults.length > 0 && searchQuery.trim() && (
                <div className="px-4 space-y-4 overflow-y-auto max-h-[calc(100vh-400px)]">
                  {searchResults.map((result, index) => (
                    <div
                      key={`${result.message.id}-${index}`}
                      onClick={() => handleJumpToMessage(result.message.id)}
                      className="cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors duration-200 border border-transparent hover:border-gray-200"
                      title="Click to jump to message"
                    >
                      <SearchResultMessage message={result.message} searchQuery={searchQuery} />
                    </div>
                  ))}
                </div>
              )}

              {/* No Results Message */}
              {isSearchActive && searchResults.length === 0 && searchQuery.trim() && (
                <div className="flex flex-col items-center justify-center py-12 text-center px-6 max-h-[calc(100vh-400px)] min-h-[calc(100vh-800px)]">
                  <div className="mb-6">
                    <NoSearchResultIcon className="w-[90px] h-[85px] mx-auto" />
                  </div>
                  <p className="text-app-grey-60 text-lg font-medium">No results found!</p>
                </div>
              )}

              {/* Regular Message List */}
              {(!isSearchActive || !searchQuery.trim()) && (
                <MessageList
                  messageActions={actions}
                  highlightedMessageId={highlightedMessageId as string | null}
                />
              )}

              <div className="mt-24 md:mt-2">
                <MessageInput
                  focus
                  overrideSubmitHandler={overrideSubmitHandler}
                  Input={MainMessagingInput}
                />
              </div>
            </>
          )}
          {activeTab === 'files' && (
            <div className="h-[calc(100vh-350px)]">
              <FilesTab
                onClose={handleFilesTabClose}
                searchResults={searchResults}
                searchQuery={searchQuery}
                isSearchActive={isSearchActive}
              />
            </div>
          )}
          {activeTab === 'starred' && <StarredMessages onClose={handleStarredMessagesClose} />}
        </Window>
      </div>
      <Thread Input={ThreadMessagingInput} />
    </div>
  )
}
